import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { motion, AnimatePresence } from "motion/react";
import { Sparkles, ArrowRight, Check, ImagePlus, Mic, MicOff, X, Chrome } from "lucide-react";
import { ONBOARDING_REQUIRED_THOUGHTS, CHROME_EXTENSION_STORE_URL } from "../lib/social/tokens";
import { completeOnboarding, recordOnboardingThought } from "../lib/social/socialApi";
import { supabase } from "../lib/supabaseClient";
import { track } from "../lib/analytics";

// PRD §4.8 — Onboarding gate. HARD GATE. No skip.
// Prompt "What are your thoughts?", at least three thoughts before access.
// "I don't know where to start" surfaces a single editorial prompt.
// Each onboarding thought may optionally include an image and/or be dictated by voice.
//
// After the three thoughts are saved, desktop Chromium users see one extra,
// fully skippable step: install the Chrome extension. (A Safari step will join
// it when that extension ships.) Everyone else goes straight in.

// Niki to provide final copy.
const STARTER_PROMPTS = [
  "What's something you keep thinking about lately?",
  "A book, song, or scene that's been living in your head?",
  "What's a question you don't have an answer to yet?",
  "What did you notice today that felt off?",
  "Something you'd want to remember six months from now?",
];

interface DraftThought {
  body: string;
  imageFile: File | null;
  imagePreviewUrl: string | null;
}

const EMPTY_DRAFT: DraftThought = { body: "", imageFile: null, imagePreviewUrl: null };

// SpeechRecognition is webkit-prefixed in Chrome/Edge; spec is unstable.
type SRConstructor = new () => SpeechRecognition;
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((this: SpeechRecognition, ev: any) => any) | null;
  onerror: ((this: SpeechRecognition, ev: any) => any) | null;
  onend: ((this: SpeechRecognition, ev: any) => any) | null;
}
function getSpeechRecognition(): SRConstructor | null {
  if (typeof window === "undefined") return null;
  const w = window as any;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as SRConstructor | null;
}

// The extension step only makes sense where the Chrome Web Store works:
// desktop Chromium (Chrome, Edge, Brave, Arc, …). UA-Client-Hints where
// available, UA-string sniff as the fallback.
function isDesktopChromium(): boolean {
  if (typeof navigator === "undefined") return false;
  const uaData = (navigator as any).userAgentData;
  if (uaData?.brands) {
    return uaData.mobile === false &&
      uaData.brands.some((b: { brand: string }) => b.brand === "Chromium");
  }
  const ua = navigator.userAgent;
  return /Chrome\/\d+/.test(ua) && !/Mobile|Android|iPhone|iPad/i.test(ua);
}

export function OnboardingGate() {
  const navigate = useNavigate();
  const [thoughts, setThoughts] = useState<DraftThought[]>([]);
  const [draft, setDraft] = useState<DraftThought>(EMPTY_DRAFT);
  const [showPrompt, setShowPrompt] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [step, setStep] = useState<"thoughts" | "extension">("thoughts");
  const [recording, setRecording] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const baseBodyRef = useRef<string>(""); // body text at recording start

  // Funnel entry: the user has reached the (unskippable) onboarding gate.
  useEffect(() => {
    track("onboarding_started");
  }, []);

  const promptIndex = useMemo(() => Math.floor(Math.random() * STARTER_PROMPTS.length), []);
  const remaining = Math.max(0, ONBOARDING_REQUIRED_THOUGHTS - thoughts.length);
  const canComplete = thoughts.length >= ONBOARDING_REQUIRED_THOUGHTS;
  const SR = getSpeechRecognition();

  // Self-heal: if the user lands here but onboarding is already complete (stale
  // client state, e.g. a grandfathered existing account where RootLayout's cached
  // query hasn't refreshed yet), redirect to the dashboard immediately rather
  // than ask them to type three new thoughts.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: userResp } = await supabase.auth.getUser();
      const uid = userResp.user?.id;
      if (!uid || cancelled) return;
      const { data } = await supabase
        .from("profiles")
        .select("onboarding_completed_at")
        .eq("id", uid)
        .maybeSingle();
      if (cancelled) return;
      if (data?.onboarding_completed_at) {
        window.dispatchEvent(new Event("burrow:onboarding-complete"));
        navigate("/", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Stop recording on unmount or when navigating away
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* noop */ }
      }
      if (draft.imagePreviewUrl) URL.revokeObjectURL(draft.imagePreviewUrl);
    };
  }, []);

  const handlePickImage = () => fileInputRef.current?.click();

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    if (draft.imagePreviewUrl) URL.revokeObjectURL(draft.imagePreviewUrl);
    setDraft((d) => ({ ...d, imageFile: file, imagePreviewUrl: URL.createObjectURL(file) }));
  };

  const removeDraftImage = () => {
    if (draft.imagePreviewUrl) URL.revokeObjectURL(draft.imagePreviewUrl);
    setDraft((d) => ({ ...d, imageFile: null, imagePreviewUrl: null }));
  };

  const startRecording = () => {
    if (!SR) {
      setVoiceError("Voice input isn't supported in this browser.");
      return;
    }
    setVoiceError(null);
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || "en-US";
    baseBodyRef.current = draft.body;

    rec.onresult = (e: any) => {
      let interim = "";
      let final = "";
      for (let i = 0; i < e.results.length; i++) {
        const transcript = e.results[i][0]?.transcript ?? "";
        if (e.results[i].isFinal) final += transcript;
        else interim += transcript;
      }
      const combined = (baseBodyRef.current + " " + final + interim).replace(/\s+/g, " ").trimStart();
      setDraft((d) => ({ ...d, body: combined }));
    };
    rec.onerror = (e: any) => {
      const code = e?.error;
      setVoiceError(
        code === "not-allowed" || code === "service-not-allowed"
          ? "Microphone permission denied."
          : code === "no-speech"
          ? "Didn't catch that — try again."
          : "Voice input failed."
      );
      setRecording(false);
    };
    rec.onend = () => setRecording(false);
    recognitionRef.current = rec;
    try {
      rec.start();
      setRecording(true);
    } catch {
      setVoiceError("Could not start microphone.");
    }
  };

  const stopRecording = () => {
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    setRecording(false);
  };

  const toggleRecording = () => (recording ? stopRecording() : startRecording());

  const handleAdd = () => {
    const text = draft.body.trim();
    if (!text && !draft.imageFile) return;
    if (recording) stopRecording();
    setThoughts((t) => [...t, draft]);
    track("onboarding_thought_added", { has_image: !!draft.imageFile, char_count: text.length });
    setDraft(EMPTY_DRAFT);
  };

  const handleRemoveSaved = (i: number) => {
    setThoughts((t) => {
      const removed = t[i];
      if (removed.imagePreviewUrl) URL.revokeObjectURL(removed.imagePreviewUrl);
      return t.filter((_, idx) => idx !== i);
    });
  };

  // Flip RootLayout's gate state BEFORE navigating, so its redirect effect
  // sees onboarding="complete" instead of stale "pending". Deliberately NOT
  // dispatched until the extension step (if any) is done — RootLayout bounces
  // /onboarding → / the moment it hears this event.
  const enterApp = () => {
    window.dispatchEvent(new Event("burrow:onboarding-complete"));
    setTimeout(() => navigate("/"), 350);
  };

  const handleComplete = async () => {
    if (!canComplete) return;
    setCompleting(true);
    try {
      for (const t of thoughts) {
        await recordOnboardingThought(t.body.trim(), t.imageFile).catch(() => undefined);
      }
      await completeOnboarding().catch(() => undefined);
      track("onboarding_completed", {
        thought_count: thoughts.length,
        image_count: thoughts.filter((t) => t.imageFile).length,
      });
    } finally {
      thoughts.forEach((t) => { if (t.imagePreviewUrl) URL.revokeObjectURL(t.imagePreviewUrl); });
      // The extension's content script stamps this attribute on burrow.world
      // pages — someone who already has it installed skips the pitch.
      const alreadyInstalled = document.documentElement.hasAttribute("data-burrow-extension");
      if (CHROME_EXTENSION_STORE_URL && !alreadyInstalled && isDesktopChromium()) {
        track("onboarding_extension_step_shown");
        setStep("extension");
        setCompleting(false);
      } else {
        enterApp();
      }
    }
  };

  const handleInstallExtension = () => {
    track("onboarding_extension_install_clicked");
    window.open(CHROME_EXTENSION_STORE_URL, "_blank", "noopener,noreferrer");
    enterApp();
  };

  const handleSkipExtension = () => {
    track("onboarding_extension_skipped");
    enterApp();
  };

  // Step 2 of 2 — skippable extension pitch, desktop Chromium only.
  if (step === "extension") {
    return (
      <div className="min-h-dvh bg-background flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-xl"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-accent/70 mb-3">
              <Sparkles size={14} />
              <span style={{ fontSize: "0.74rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                One more thing
              </span>
            </div>
            <h1
              className="text-primary"
              style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "2.4rem", fontWeight: 400, lineHeight: 1.1 }}
            >
              Capture from anywhere
            </h1>
            {/* Niki to provide final copy. */}
            <p className="text-muted-foreground/80 mt-4" style={{ fontSize: "0.92rem", lineHeight: 1.55 }}>
              The Burrow extension clips ideas, quotes, and pages into your library
              while you browse — one click, no tab-switching.
            </p>
          </div>

          <div className="bg-card/70 border border-border/40 rounded-3xl p-6 flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-accent/10 text-accent flex items-center justify-center">
              <Chrome size={24} />
            </div>
            <button
              onClick={handleInstallExtension}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              style={{ fontSize: "0.92rem", fontFamily: "'Fraunces', Georgia, serif" }}
            >
              <span>Add to Chrome</span>
              <ArrowRight size={15} />
            </button>
            <p className="text-muted-foreground/50" style={{ fontSize: "0.72rem" }}>
              Safari is on the way.
            </p>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={handleSkipExtension}
              className="text-muted-foreground/70 hover:text-foreground transition-colors"
              style={{ fontSize: "0.82rem", fontFamily: "'Fraunces', Georgia, serif" }}
            >
              Skip for now
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-xl"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-accent/70 mb-3">
            <Sparkles size={14} />
            <span style={{ fontSize: "0.74rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
              Welcome to Burrow
            </span>
          </div>
          <h1
            className="text-primary"
            style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "2.4rem", fontWeight: 400, lineHeight: 1.1 }}
          >
            What are your thoughts?
          </h1>
          <p className="text-muted-foreground/80 mt-4" style={{ fontSize: "0.92rem", lineHeight: 1.55 }}>
            Capture three to begin. They stay private until you decide otherwise.
          </p>
        </div>

        <div className="bg-card/70 border border-border/40 rounded-3xl p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-muted-foreground" style={{ fontSize: "0.82rem" }}>
              {thoughts.length} of {ONBOARDING_REQUIRED_THOUGHTS}
            </div>
            <button
              onClick={() => setShowPrompt((s) => !s)}
              className="text-accent/80 hover:text-accent transition-colors"
              style={{ fontSize: "0.78rem", fontFamily: "'Fraunces', Georgia, serif" }}
            >
              I don't know where to start
            </button>
          </div>

          <AnimatePresence>
            {showPrompt && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden mb-3"
              >
                <div className="rounded-2xl bg-accent/8 px-4 py-3 text-foreground/80 italic" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.92rem" }}>
                  {STARTER_PROMPTS[promptIndex]}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Image preview */}
          {draft.imagePreviewUrl && (
            <div className="mb-3 relative inline-block">
              <img
                src={draft.imagePreviewUrl}
                alt=""
                className="rounded-2xl max-h-48 object-cover border border-border/40"
              />
              <button
                onClick={removeDraftImage}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-foreground/80 text-background hover:bg-foreground flex items-center justify-center shadow-md transition-colors"
                aria-label="Remove image"
              >
                <X size={13} />
              </button>
            </div>
          )}

          <textarea
            value={draft.body}
            onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleAdd();
              }
            }}
            rows={4}
            placeholder="A thought, a question, something you noticed..."
            className="w-full bg-background/60 border border-border/40 rounded-2xl px-4 py-3 outline-none focus:border-accent/50 transition-colors text-foreground"
            style={{ fontSize: "0.92rem", lineHeight: 1.55, fontFamily: "'Fraunces', Georgia, serif" }}
          />

          {/* Action row */}
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <button
                onClick={handlePickImage}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
                style={{ fontSize: "0.78rem" }}
                title="Add an image"
              >
                <ImagePlus size={14} />
                <span className="hidden sm:inline">Image</span>
              </button>
              <button
                onClick={toggleRecording}
                disabled={!SR}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl transition-all ${
                  recording
                    ? "bg-accent/15 text-accent"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
                } ${!SR ? "opacity-40 cursor-not-allowed" : ""}`}
                style={{ fontSize: "0.78rem" }}
                title={SR ? (recording ? "Stop dictation" : "Dictate (speech to text)") : "Voice input not supported"}
              >
                {recording ? <MicOff size={14} /> : <Mic size={14} />}
                <span className="hidden sm:inline">{recording ? "Stop" : "Voice"}</span>
                {recording && (
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" aria-hidden />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>

            <div className="flex items-center gap-3">
              <span className="text-muted-foreground/60" style={{ fontSize: "0.74rem" }}>
                ⌘/Ctrl + Enter to add
              </span>
              <button
                onClick={handleAdd}
                disabled={!draft.body.trim() && !draft.imageFile}
                className="px-4 py-2 rounded-xl bg-accent text-accent-foreground hover:opacity-90 transition-opacity disabled:opacity-40"
                style={{ fontSize: "0.86rem", fontFamily: "'Fraunces', Georgia, serif" }}
              >
                Add
              </button>
            </div>
          </div>

          {voiceError && (
            <p className="mt-2 text-destructive/80" style={{ fontSize: "0.74rem" }}>
              {voiceError}
            </p>
          )}

          {thoughts.length > 0 && (
            <ul className="mt-5 space-y-2">
              {thoughts.map((t, i) => (
                <li key={i} className="flex items-start gap-2 px-3 py-2 rounded-xl bg-background/40 group">
                  <Check size={14} className="text-accent shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    {t.imagePreviewUrl && (
                      <img
                        src={t.imagePreviewUrl}
                        alt=""
                        className="w-9 h-9 rounded-lg object-cover shrink-0"
                      />
                    )}
                    <span className="text-foreground/80 line-clamp-2 flex-1" style={{ fontSize: "0.86rem", lineHeight: 1.45 }}>
                      {t.body || <span className="italic text-muted-foreground/60">image only</span>}
                    </span>
                  </div>
                  <button
                    onClick={() => handleRemoveSaved(i)}
                    className="text-muted-foreground/40 hover:text-destructive opacity-0 group-hover:opacity-100 transition-all p-1"
                    aria-label="Remove"
                  >
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <span className="text-muted-foreground/70" style={{ fontSize: "0.82rem" }}>
            {canComplete ? "You're ready." : `${remaining} more to go.`}
          </span>
          <button
            onClick={handleComplete}
            disabled={!canComplete || completing}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-40"
            style={{ fontSize: "0.92rem", fontFamily: "'Fraunces', Georgia, serif" }}
          >
            <span>{completing ? "Saving…" : "Enter Burrow"}</span>
            <ArrowRight size={15} />
          </button>
        </div>

        <p className="text-center text-muted-foreground/50 mt-6" style={{ fontSize: "0.72rem" }}>
          No skip. No "do this later." Three thoughts and you're in.
        </p>
      </motion.div>
    </div>
  );
}
